/* eslint-disable react/destructuring-assignment */
/* eslint-disable react/no-array-index-key */
import { DataGrid, GridToolbarContainer, GridToolbarColumnsButton, GridToolbarFilterButton, GridToolbarDensitySelector, GridToolbarExport, frFR } from '@mui/x-data-grid';
import { Typography, Link, Card, Box, Avatar, IconButton, Tooltip, DialogContentText, DialogActions, DialogContent, Dialog, Button, useTheme } from '@material-ui/core';
import { Link as RouterLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import EventRoundedIcon from '@mui/icons-material/EventRounded';
import GroupsRoundedIcon from '@mui/icons-material/Groups';
import ComputerRoundedIcon from '@mui/icons-material/Computer';
import LayersRoundedIcon from '@mui/icons-material/Layers';
import SchoolRoundedIcon from '@mui/icons-material/School';
import NorthEastRoundedIcon from '@mui/icons-material/NorthEastRounded';
import EuroRoundedIcon from '@mui/icons-material/EuroRounded';
import useAuth from '../../../hooks/useAuth';
import { formationApi } from '../../../api/formationApi';
import toast from 'react-hot-toast';
import { useState } from 'react';

// ── Palette de dégradés déterministes pour les avatars ──
const AVATAR_GRADIENTS = [
  ['#3b82f6', '#1d4ed8'],
  ['#8b5cf6', '#6d28d9'],
  ['#06b6d4', '#0e7490'],
  ['#10b981', '#047857'],
  ['#f59e0b', '#b45309'],
  ['#ec4899', '#be185d'],
  ['#6366f1', '#4338ca'],
  ['#14b8a6', '#0f766e'],
];

const hashString = (str = '') => {
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    // eslint-disable-next-line no-bitwise
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash);
};

const getInitials = (name = '') => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'SE';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

// ── Pastille générique ──
const Pill = ({ icon, label, color, bg, border, mono }) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      px: 1.1,
      py: 0.5,
      borderRadius: '9px',
      backgroundColor: bg,
      color,
      border: '1px solid',
      borderColor: border,
      fontSize: '0.7rem',
      fontWeight: 700,
      lineHeight: 1.2,
      fontFamily: mono ? '"Roboto Mono", monospace' : 'inherit',
      letterSpacing: mono ? '0.02em' : 0,
      maxWidth: '100%',
    }}
  >
    {icon}
    <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {label}
    </Box>
  </Box>
);

Pill.propTypes = {
  icon: PropTypes.node,
  label: PropTypes.node,
  color: PropTypes.string,
  bg: PropTypes.string,
  border: PropTypes.string,
  mono: PropTypes.bool,
};

const NEUTRAL = { color: '#64748b', bg: 'rgba(100, 116, 139, 0.07)', border: 'rgba(100, 116, 139, 0.16)' };

const getStatusStyle = (val = '') => {
  if (val === 'Active') return { color: '#059669', bg: 'rgba(16, 185, 129, 0.1)', border: 'rgba(16, 185, 129, 0.25)' };
  if (val === 'Annulée') return { color: '#d97706', bg: 'rgba(245, 158, 11, 0.12)', border: 'rgba(245, 158, 11, 0.25)' };
  if (val === 'Offerte') return { color: '#2563eb', bg: 'rgba(59, 130, 246, 0.1)', border: 'rgba(59, 130, 246, 0.25)' };
  return NEUTRAL;
};

const getFormatStyle = (val = '') => {
  if (val.includes('E-Learning') || val.includes('E-learning')) {
    return { color: '#6366f1', bg: 'rgba(99, 102, 241, 0.09)', border: 'rgba(99, 102, 241, 0.22)', icon: <ComputerRoundedIcon sx={{ fontSize: '0.9rem' }} /> };
  }
  if (val.includes('Présentiel')) {
    return { color: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.09)', border: 'rgba(139, 92, 246, 0.22)', icon: <GroupsRoundedIcon sx={{ fontSize: '0.95rem' }} /> };
  }
  if (val.includes('Mixte') || val.includes('virtuelle')) {
    return { color: '#0d9488', bg: 'rgba(20, 184, 166, 0.09)', border: 'rgba(20, 184, 166, 0.22)', icon: <LayersRoundedIcon sx={{ fontSize: '0.9rem' }} /> };
  }
  return { ...NEUTRAL, icon: <SchoolRoundedIcon sx={{ fontSize: '0.9rem' }} /> };
};

const Dash = () => <Typography sx={{ fontSize: '0.8rem', color: 'text.disabled' }}>—</Typography>;

const DatePill = ({ value, icon }) => {
  if (!value) return <Dash />;
  return (
    <Pill
      icon={icon}
      label={value}
      color="#475569"
      bg="rgba(100, 116, 139, 0.07)"
      border="rgba(100, 116, 139, 0.16)"
      mono
    />
  );
};

DatePill.propTypes = {
  value: PropTypes.node,
  icon: PropTypes.node,
};

const SessionFormationResultTable = (props) => {
  const { sessionsFormation, setSessionsFormation, ...other } = props;
  const rows = [];
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [currentId, setCurrentId] = useState(0);
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';

  const handleClickOpen = (id) => { setCurrentId(id); setOpen(true); };
  const handleClose = () => setOpen(false);

  const handleDelete = async (id) => {
    try {
      await formationApi.deleteSession(id, localStorage.getItem('accessToken'));
      const tempSessions = [...sessionsFormation];
      tempSessions.splice(tempSessions.findIndex((sess) => sess.id === id), 1);
      setSessionsFormation(tempSessions);
      toast.success('Session supprimée !');
      handleClose();
    } catch (err) {
      console.error(err);
      toast.error('Il y a eu un souci lors de la suppression !');
    }
  };

  const toolbarSx = {
    p: 1.5,
    px: 2,
    borderBottom: '1px solid',
    borderColor: isLight ? 'rgba(226, 232, 240, 0.7)' : 'rgba(255, 255, 255, 0.06)',
    display: 'flex',
    gap: 0.5,
    backgroundColor: isLight ? 'rgba(248, 250, 252, 0.6)' : 'rgba(15, 23, 42, 0.25)',
    '& .MuiButton-root': {
      color: '#3b82f6',
      fontWeight: 700,
      fontSize: '0.78rem',
      textTransform: 'none',
      borderRadius: '10px',
      px: 1.5,
      transition: 'all 0.15s',
      '&:hover': { backgroundColor: 'rgba(59, 130, 246, 0.08)' },
    },
  };

  const CustomToolbar = () => (
    <GridToolbarContainer sx={toolbarSx}>
      <GridToolbarDensitySelector />
      {user.id_service === 0 && <GridToolbarExport />}
    </GridToolbarContainer>
  );

  sessionsFormation.forEach((item) => {
    // eslint-disable-next-line no-unused-expressions
    item.name !== null
      ? rows.push({
        id: item.id,
        name: item.name,
        numberSession: item.number_session,
        startDate: item.start_date,
        endDate: item.end_date,
        meetingStartDate: item.meeting_start_date,
        meetingEndDate: item.meeting_end_date,
        format: item.format,
        status: item.status,
        billingSent: item.billing_sent,
        convocationSentAt: item.convocation_sent_at,
        nombre: item.nombre,
        nombreInscrits: item.nombre_inscrits,
        conventionsSignees: item.conventions_signees,
        caNet: item.ca_net,
        charge: item.charge,
        resultat: item.resultat,
        image: item.image,
        programNumber: item.program_number,
        idFormation: item.idFormation
      }) : null;
  });

  const columns = [
    {
      field: 'name',
      headerName: 'Session',
      width: 420,
      renderCell: (cellValues) => {
        const name = cellValues.row.name || '';
        const grad = AVATAR_GRADIENTS[hashString(name) % AVATAR_GRADIENTS.length];
        const num = cellValues.row.numberSession;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', py: 0.5, width: '100%' }}>
            <Avatar
              src={cellValues.row.image || undefined}
              sx={{
                height: 46,
                width: 46,
                borderRadius: '14px',
                fontSize: '0.95rem',
                fontWeight: 800,
                color: '#fff',
                boxShadow: `0 6px 16px ${grad[0]}40`,
                border: isLight ? '2px solid #fff' : '2px solid rgba(255,255,255,0.08)',
                background: `linear-gradient(135deg, ${grad[0]} 0%, ${grad[1]} 100%)`,
                flexShrink: 0,
              }}
            >
              {getInitials(name)}
            </Avatar>
            <Box sx={{ ml: 1.75, minWidth: 0 }}>
              <Tooltip
                title={name}
                arrow
                componentsProps={{ tooltip: { sx: { fontSize: '0.74rem', fontWeight: 600, borderRadius: '8px', maxWidth: 360 } } }}
              >
                <Link
                  component={RouterLink}
                  to={`/dashboard/session/${cellValues.row.id}`}
                  sx={{
                    textDecoration: 'none',
                    fontWeight: 700,
                    fontSize: '0.78rem',
                    color: 'text.primary',
                    lineHeight: 1.3,
                    transition: 'color 0.15s',
                    display: '-webkit-box',
                    WebkitLineClamp: 4,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    wordBreak: 'break-word',
                    '&:hover': { color: '#3b82f6' },
                  }}
                >
                  {name}
                </Link>
              </Tooltip>
              <Box sx={{ mt: 0.4, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    px: 0.7,
                    py: 0.2,
                    borderRadius: '7px',
                    backgroundColor: 'rgba(59, 130, 246, 0.08)',
                    border: '1px solid rgba(59, 130, 246, 0.2)',
                    color: '#2563eb',
                    fontSize: '0.66rem',
                    fontWeight: 700,
                    fontFamily: '"Roboto Mono", monospace',
                    letterSpacing: '0.02em',
                  }}
                >
                  Session N° {num || '—'}
                </Box>
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    px: 0.7,
                    py: 0.2,
                    borderRadius: '7px',
                    backgroundColor: 'rgba(100, 116, 139, 0.08)',
                    border: '1px solid rgba(100, 116, 139, 0.18)',
                    color: '#475569',
                    fontSize: '0.66rem',
                    fontWeight: 700,
                    fontFamily: '"Roboto Mono", monospace',
                    letterSpacing: '0.02em',
                  }}
                >
                  N° Prog. {cellValues.row.programNumber && cellValues.row.programNumber !== 'N/A' ? cellValues.row.programNumber : '—'}
                </Box>
                <Tooltip
                  title={`${cellValues.row.conventionsSignees || 0} convention(s) signée(s) / ${cellValues.row.nombreInscrits || 0} apprenant(s) inscrit(s) (Inscrit + InscritD)`}
                  arrow
                  componentsProps={{ tooltip: { sx: { fontSize: '0.72rem', fontWeight: 600, borderRadius: '8px', maxWidth: 320 } } }}
                >
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.4,
                      maxWidth: 200,
                      px: 0.7,
                      py: 0.2,
                      borderRadius: '7px',
                      backgroundColor: 'rgba(16, 185, 129, 0.09)',
                      border: '1px solid rgba(16, 185, 129, 0.22)',
                      color: '#059669',
                      fontSize: '0.66rem',
                      fontWeight: 700,
                      letterSpacing: '0.02em',
                    }}
                  >
                    <GroupsRoundedIcon sx={{ fontSize: '0.85rem' }} />
                    <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {cellValues.row.conventionsSignees || 0} / {cellValues.row.nombreInscrits || 0}
                    </Box>
                  </Box>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        );
      },
    },
    { field: 'startDate', headerName: 'Date de début', width: 160, renderCell: (cellValues) => (<DatePill value={cellValues.row.startDate} icon={<EventRoundedIcon sx={{ fontSize: '0.9rem' }} />} />) },
    { field: 'endDate', headerName: 'Date de fin', width: 160, renderCell: (cellValues) => (<DatePill value={cellValues.row.endDate} icon={<EventRoundedIcon sx={{ fontSize: '0.9rem' }} />} />) },
    { field: 'meetingStartDate', headerName: 'Début V/P', width: 175, renderCell: (cellValues) => (<DatePill value={cellValues.row.meetingStartDate} icon={<AccessTimeRoundedIcon sx={{ fontSize: '0.9rem' }} />} />) },
    { field: 'meetingEndDate', headerName: 'Fin V/P', width: 175, renderCell: (cellValues) => (<DatePill value={cellValues.row.meetingEndDate} icon={<AccessTimeRoundedIcon sx={{ fontSize: '0.9rem' }} />} />) },
    {
      field: 'status',
      headerName: 'Statut',
      width: 140,
      renderCell: (cellValues) => {
        const val = cellValues.row.status || '';
        if (!val) return <Dash />;
        const s = getStatusStyle(val);
        return (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.6,
              px: 1.1,
              py: 0.5,
              borderRadius: '9px',
              backgroundColor: s.bg,
              color: s.color,
              border: '1px solid',
              borderColor: s.border,
              fontSize: '0.72rem',
              fontWeight: 800,
            }}
          >
            <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: s.color }} />
            {val}
          </Box>
        );
      },
    },
    {
      field: 'format',
      headerName: 'Format',
      width: 175,
      renderCell: (cellValues) => {
        const val = cellValues.row.format || '';
        if (!val) return <Dash />;
        const s = getFormatStyle(val);
        return <Pill icon={s.icon} label={val} color={s.color} bg={s.bg} border={s.border} />;
      },
    },
    {
      field: 'billingSent',
      headerName: 'Facturation',
      width: 140,
      renderCell: (cellValues) => {
        const isSent = !!cellValues.row.billingSent;
        const color = isSent ? '#059669' : '#64748b';
        const bg = isSent ? 'rgba(16, 185, 129, 0.1)' : 'rgba(100, 116, 139, 0.07)';
        const border = isSent ? 'rgba(16, 185, 129, 0.25)' : 'rgba(100, 116, 139, 0.16)';
        return (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.6,
              px: 1.1,
              py: 0.5,
              borderRadius: '9px',
              backgroundColor: bg,
              color,
              border: '1px solid',
              borderColor: border,
              fontSize: '0.72rem',
              fontWeight: 800,
            }}
          >
            <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: color }} />
            {isSent ? 'Oui' : 'Non'}
          </Box>
        );
      }
    },
    {
      field: 'convocationSentAt',
      headerName: 'Convocation envoyée',
      width: 190,
      renderCell: (cellValues) => {
        const dateStr = cellValues.row.convocationSentAt;
        if (!dateStr) return <Dash />;
        return (
          <Pill
            icon={<EventRoundedIcon sx={{ fontSize: '0.9rem' }} />}
            label={dateStr}
            color="#0d9488"
            bg="rgba(20, 184, 166, 0.09)"
            border="rgba(20, 184, 166, 0.22)"
            mono
          />
        );
      }
    },
    {
      field: 'caNet',
      headerName: 'CA net',
      width: 150,
      // CA des apprenants Inscrit + InscritD (somme des prix réels)
      valueGetter: (cellValues) => parseFloat(cellValues.row.caNet) || 0,
      renderCell: (cellValues) => (
        <Pill
          icon={<EuroRoundedIcon sx={{ fontSize: '0.9rem' }} />}
          label={`${(parseFloat(cellValues.row.caNet) || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`}
          color="#047857"
          bg="rgba(16, 185, 129, 0.09)"
          border="rgba(16, 185, 129, 0.22)"
          mono
        />
      ),
    },
    {
      field: 'charge',
      headerName: 'Charge',
      width: 150,
      // Charges de l'onglet facturation (formateur + frais + restauration/livret par
      // apprenant Inscrit/InscritD + commission % du CA), calculées côté serveur.
      valueGetter: (cellValues) => parseFloat(cellValues.row.charge) || 0,
      renderCell: (cellValues) => (
        <Pill
          icon={<EuroRoundedIcon sx={{ fontSize: '0.9rem' }} />}
          label={`${(parseFloat(cellValues.row.charge) || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`}
          color="#b45309"
          bg="rgba(245, 158, 11, 0.10)"
          border="rgba(245, 158, 11, 0.25)"
          mono
        />
      ),
    },
    {
      field: 'resultat',
      headerName: 'Résultat',
      width: 160,
      // Résultat = CA net − Charge. Vert si positif, rouge si négatif.
      valueGetter: (cellValues) => parseFloat(cellValues.row.resultat) || 0,
      renderCell: (cellValues) => {
        const val = parseFloat(cellValues.row.resultat) || 0;
        const positive = val >= 0;
        return (
          <Pill
            icon={<EuroRoundedIcon sx={{ fontSize: '0.9rem' }} />}
            label={`${positive ? '+' : ''}${val.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`}
            color={positive ? '#047857' : '#b91c1c'}
            bg={positive ? 'rgba(16, 185, 129, 0.09)' : 'rgba(239, 68, 68, 0.09)'}
            border={positive ? 'rgba(16, 185, 129, 0.22)' : 'rgba(239, 68, 68, 0.25)'}
            mono
          />
        );
      },
    },
    {
      field: 'actions',
      headerName: '',
      disableExport: true,
      sortable: false,
      filterable: false,
      width: 110,
      renderCell: (cellValues) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title="Voir la formation" arrow>
            <IconButton
              component={RouterLink}
              to={`/dashboard/formations/${cellValues.row.idFormation}`}
              size="small"
              sx={{
                color: 'text.secondary',
                borderRadius: '10px',
                transition: 'all 0.18s',
                '&:hover': { backgroundColor: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', transform: 'translateY(-1px)' },
              }}
            >
              <NorthEastRoundedIcon sx={{ fontSize: '1.1rem' }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Supprimer" arrow>
            <span>
              <IconButton
                onClick={() => handleClickOpen(cellValues.row.id)}
                disabled={user.access_level !== 0}
                size="small"
                sx={{
                  color: 'text.secondary',
                  borderRadius: '10px',
                  transition: 'all 0.18s',
                  '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', transform: 'translateY(-1px)' },
                }}
              >
                <DeleteOutlineIcon sx={{ fontSize: '1.2rem' }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Card
      sx={{
        border: '1px solid',
        borderColor: isLight ? 'rgba(226, 232, 240, 0.6)' : 'rgba(255, 255, 255, 0.06)',
        borderRadius: '24px',
        overflow: 'hidden',
        boxShadow: isLight ? '0 12px 40px rgba(15, 23, 42, 0.05)' : '0 12px 40px rgba(0, 0, 0, 0.3)',
        background: isLight ? '#fff' : 'rgba(30, 41, 59, 0.45)',
      }}
      {...other}
    >
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{ style: { borderRadius: '20px', padding: '10px' } }}
      >
        <DialogContent>
          <DialogContentText sx={{ fontWeight: 600, color: 'text.primary' }}>
            Souhaitez-vous vraiment supprimer cette session ? Cette action est irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => handleDelete(currentId)}
            sx={{
              color: '#ef4444',
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: '10px',
              '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.05)' },
            }}
          >
            Supprimer
          </Button>
          <Button
            onClick={handleClose}
            autoFocus
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              color: '#fff',
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: '10px',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)',
              '&:hover': { background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)' },
            }}
          >
            Annuler
          </Button>
        </DialogActions>
      </Dialog>
      <DataGrid
        sx={{
          border: 'none',
          fontFamily: 'inherit',
          // ── En-têtes ──
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: isLight ? 'rgba(248, 250, 252, 0.9)' : 'rgba(15, 23, 42, 0.4)',
            borderBottom: '1px solid',
            borderColor: isLight ? 'rgba(226, 232, 240, 0.8)' : 'rgba(255, 255, 255, 0.08)',
            borderRadius: 0,
            minHeight: '52px !important',
            maxHeight: '52px !important',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 800,
            fontSize: '0.7rem',
            textTransform: 'uppercase',
            letterSpacing: '0.07em',
            color: 'text.secondary',
          },
          '& .MuiDataGrid-columnSeparator': { display: 'none' },
          // ── Lignes ──
          '& .MuiDataGrid-row': {
            transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
          },
          '& .MuiDataGrid-row:nth-of-type(even)': {
            backgroundColor: isLight ? 'rgba(248, 250, 252, 0.45)' : 'rgba(255, 255, 255, 0.015)',
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: isLight ? 'rgba(59, 130, 246, 0.05)' : 'rgba(59, 130, 246, 0.09)',
            boxShadow: 'inset 3px 0 0 0 #3b82f6',
          },
          // ── Cellules ──
          '& .MuiDataGrid-cell': {
            borderColor: isLight ? 'rgba(226, 232, 240, 0.4)' : 'rgba(255, 255, 255, 0.04)',
            whiteSpace: 'normal !important',
            wordBreak: 'break-word !important',
            display: 'flex !important',
            alignItems: 'center !important',
          },
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none !important' },
          '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': { outline: 'none !important' },
          '& .MuiDataGrid-cell[data-field="name"]': {
            whiteSpace: 'normal !important',
            lineHeight: '1.3 !important',
            py: '8px !important',
          },
          // ── Pied / pagination ──
          '& .MuiDataGrid-footerContainer': {
            borderTop: '1px solid',
            borderColor: isLight ? 'rgba(226, 232, 240, 0.8)' : 'rgba(255, 255, 255, 0.08)',
            backgroundColor: isLight ? 'rgba(248, 250, 252, 0.5)' : 'rgba(15, 23, 42, 0.3)',
          },
          '& .MuiTablePagination-root': { color: 'text.secondary' },
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontWeight: 600, fontSize: '0.78rem' },
          '& .MuiDataGrid-selectedRowCount': { color: '#3b82f6', fontWeight: 600 },
          '& .MuiDataGrid-virtualScroller': { backgroundColor: 'transparent' },
        }}
        localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
        initialState={{
          sorting: {
            sortModel: [{ field: 'name', sort: 'asc' }],
          },
        }}
        autoHeight
        rowHeight={112}
        rows={rows}
        columns={columns}
        pageSize={20}
        rowsPerPageOptions={[30, 100]}
        disableSelectionOnClick
        components={{ Toolbar: CustomToolbar }}
      />
    </Card>
  );
};
SessionFormationResultTable.propTypes = {
  sessionsFormation: PropTypes.array.isRequired,
  setSessionsFormation: PropTypes.func.isRequired
};

export default SessionFormationResultTable;
